import { useArticles } from "@faire/help-center-utils/hooks/article/useArticles";
import { useStrictLocalization } from "@faire/web--source/common/localization";

import { SelfServeNodes } from "../../../types";
import { addOrEditItems } from "../../instantAnswers/orders-addOrEditItems";
import { cancelOrder } from "../../instantAnswers/orders-cancelOrder";
import { editOrderInformation } from "../../instantAnswers/orders-editOrderInformation";
import { insiderFreeShipping } from "../../instantAnswers/orders-insiderFreeShipping";
import { internationalOrderSettings } from "../../instantAnswers/orders-internationalOrderSettings";
import { questionAboutCustomer } from "../../instantAnswers/orders-questionAboutCustomer";
import { RequestCommissionChanges } from "../../instantAnswers/orders-requestCommissionChanges";
import { splitOrder } from "../../instantAnswers/orders-splitOrder";
import { orderNewStateVisibilityFn } from "../../visibilityFunctions/orderStateVisibilityFn";

export const useOrderNewBrandNodes = (): SelfServeNodes => {
  const { strictLocalize } = useStrictLocalization();
  const articles = useArticles();
  return {
    QuestionAboutCustomerNewState: {
      id: "QuestionAboutCustomerNewState",
      label: strictLocalize({
        defaultMessage: "Find customer information",
        description: {
          text: "A node in the Orders section of the full page self-serve experience of Faire's Help Center",
        },
      }),
      level: 2,
      isNodeVisible: orderNewStateVisibilityFn,
      instantAnswer: questionAboutCustomer(strictLocalize),
    },
    AddOrEditItemsNewState: {
      id: "AddOrEditItemsNewState",
      label: strictLocalize({
        defaultMessage: "Add or edit items in order",
        description: {
          text: "A node in the Orders section of the full page self-serve experience of Faire's Help Center",
        },
      }),
      level: 2,
      isNodeVisible: orderNewStateVisibilityFn,
      instantAnswer: addOrEditItems(strictLocalize),
      articles: [articles.FulfillingOrders.EDIT_RETAILER_ORDER],
    },
    EditOrderInformationNewState: {
      id: "EditOrderInformationNewState",
      label: strictLocalize({
        defaultMessage: "Edit order information",
        description: {
          text: "A node in the Orders section of the full page self-serve experience of Faire's Help Center",
        },
      }),
      level: 2,
      isNodeVisible: orderNewStateVisibilityFn,
      instantAnswer: editOrderInformation(strictLocalize),
      articles: [articles.FulfillingOrders.EDIT_RETAILER_ORDER],
    },
    InternationalOrderSettingsNewState: {
      id: "InternationalOrderSettingsNewState",
      label: strictLocalize({
        defaultMessage: "International order settings",
        description: {
          text: "A node in the Orders section of the full page self-serve experience of Faire's Help Center",
        },
      }),
      level: 2,
      isNodeVisible: orderNewStateVisibilityFn,
      instantAnswer: internationalOrderSettings(strictLocalize),
      articles: [articles.International.LOCATION_SHOP_LIVE],
    },
    RequestCommissionChangesNewState: {
      id: "RequestCommissionChangesNewState",
      label: strictLocalize({
        defaultMessage: "Request commission change",
        description: {
          text: "A node in the Orders section of the full page self-serve experience of Faire's Help Center",
        },
      }),
      level: 2,
      isNodeVisible: orderNewStateVisibilityFn,
      instantAnswer: RequestCommissionChanges(strictLocalize),
      articles: [articles.FaireDirect.ZERO_COMMISSION],
    },
    InsiderFreeShippingNewState: {
      id: "InsiderFreeShippingNewState",
      label: strictLocalize({
        defaultMessage: "Insider free shipping",
        description: {
          text: "A node in the Orders section of the full page self-serve experience of Faire's Help Center",
        },
      }),
      level: 2,
      instantAnswer: insiderFreeShipping(strictLocalize),
      articles: [articles.INSIDER.INSIDER_BRAND_PARTNERSHIP],
      isNodeVisible: orderNewStateVisibilityFn,
    },
    SplitOrderNewState: {
      id: "SplitOrderNewState",
      label: strictLocalize({
        defaultMessage: "Split order",
        description: {
          text: "A node in the Orders section of the full page self-serve experience of Faire's Help Center",
        },
      }),
      level: 2,
      instantAnswer: splitOrder(strictLocalize),
      articles: [
        articles.FulfillingOrders.SPLIT_ATS_ORDER,
        articles.FulfillingOrders.SPLIT_PREORDER,
      ],
      isNodeVisible: orderNewStateVisibilityFn,
    },
    CancelOrderNewState: {
      id: "CancelOrderNewState",
      label: strictLocalize({
        defaultMessage: "Cancel order",
        description: {
          text: "A node in the Orders section of the full page self-serve experience of Faire's Help Center",
        },
      }),
      level: 2,
      isNodeVisible: orderNewStateVisibilityFn,
      instantAnswer: cancelOrder(strictLocalize),
      articles: [articles.FulfillingOrders.FULFILLING_ORDER],
    },
  };
};
